import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import { Timestamp, toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import type {
    MetricConsumptionTimeSeries,
    CreditUsageDataPoint,
    MetricConsumptionTimeSeries_Kind,
} from "gitpod-next-api/gitpod/v1/billing_pb";
import { DateRange } from "gitpod-next-api/gitpod/v1/usage_pb";
import { queryOptions, useQuery } from "@tanstack/react-query";

export type PlainMetricConsumptionTimeSeries = PlainMessage<MetricConsumptionTimeSeries>;
export type PlainCreditUsageDataPoint = PlainMessage<CreditUsageDataPoint>;

const creditUsageKeys = {
    consumption: (organizationId: string, days: number) =>
        keyWithPrincipal(["credit-usage", "consumption", organizationId, days]),
};

export function useCreditConsumptionTimeSeriesOptions(organizationId: string, days = 7) {
    const api = useGitpodAPI();

    return queryOptions({
        queryKey: creditUsageKeys.consumption(organizationId, days),
        queryFn: async () => {
            const now = new Date();
            const start = new Date(now);
            start.setDate(start.getDate() - days);

            const response = await api.billingService.getCreditConsumptionTimeSeries({
                organizationId,
                dateRange: new DateRange({
                    startTime: Timestamp.fromDate(start),
                    endTime: Timestamp.fromDate(now),
                }),
            });
            return toPlainMessage(response);
        },
        throwOnError: false,
        enabled: !!organizationId,
    });
}

export function useCreditConsumptionTimeSeries(organizationId: string, days = 7) {
    return useQuery(useCreditConsumptionTimeSeriesOptions(organizationId, days));
}

export function filterMetricsByKind(
    metrics: PlainMetricConsumptionTimeSeries[] | undefined,
    kind: MetricConsumptionTimeSeries_Kind,
): PlainMetricConsumptionTimeSeries | undefined {
    return metrics?.find((m) => m.kind === kind);
}
